import "./AdminStatistics.scss";
import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import {
  CourtOccupancyStatisticsExport,
  IUser,
  MemberStatisticsExport,
  NoShowOccupancyStatisticsExport,
  OccupancyStatisticsExport,
  ActivityOccupancyStatisticsExport,
  CoachStatisticsExport,
} from "../../types/api";

import WeekDaySelector from "../../hoc/DatePicker/Datepicker";
import {
  exportStatisticsOccupancySlots,
  getActivityOccupancySlots,
  getCoachOccupancySlots,
  getCourtOccupancySlots,
  getMemberOccupancySlots,
  getNoShowOccupancySlots,
  getOccupancySlots,
  getRehabOccupancySlots,
} from "../../api/statistics/statistics";
import AdminOccupancyModule from "./AdminOccupancyModule/AdminOccupancyModule";
import { combinedQuery } from "../../api/combinedQueries/combinedQueries";
import {
  createStatisticsFilterStore,
  StatisticsFilterStore,
} from "./Stores/statisticsFilterStoreFactory";
import { OccupancyAggregationType } from "../../shared/enums";
import AdminRehabOccupancyModule from "./AdminRehabOccupancyModule/AdminRehabOccupancyModule";
import AdminMembersOccupancyModule from "./AdminMembersOccupancyModule/AdminMembersOccupancyModule";
import AdminCourtsOccupancyModule from "./AdminCourtsOccupancyModule/AdminCourtsOccupancyModule";
import AdminNoShowOccupancyModule from "./AdminNoShowOccupancyModule/AdminNoShowOccupancyModule";
import AdminCoachOccupancyModule from "./AdminCoachOccupancyModule/AdminCoachOccupancyModule";
import AdminActivityOccupancyModule from "./AdminActivityOccupancyModule/AdminActivityOccupancyModule";
import ChooseUserPopup from "../../containers/Admin/ChooseUserPopup/ChooseUserPopup";
import ExportStatisticsPopup from "../../containers/Statistics/ExportStatisticsPopup/ExportStatisticsPopup";
import AdminExportsModule from "./AdminExportsModule/AdminExportsModule";

export enum MemberExportType {
  DEFAULT = "DEFAULT",
  INCLUDE_NULL = "INCLUDE_NULL",
}
const buildFiltersFromStore = (
  store: StatisticsFilterStore,
  useCourts: boolean = true,
  useRoles: boolean = true,
  useToplistFilter: boolean = false
) => {
  const state = store.getState();

  const baseFilters: any = {
    permissionIds: state.selectedPermissions?.map((p) => p.permission_id) ?? [],
    genders: state.genders,
    ageRange: {
      min: state.ageStart,
      max: state.ageEnd,
    },
    timeRange: {
      start_time: state.startTime + ":00",
      end_time: state.endTime + ":00",
    },
    activityColors: state.activityColors,
    coachIds: state.selectedCoaches?.map((c) => c.coach_id) ?? [],
  };

  if (useCourts) {
    baseFilters.courtIds = state.selectedCourts?.map((c) => c.court_id) ?? [];
  }

  if (useRoles) {
    baseFilters.typeOfBookings = state.typeOfBookings?.map((r) => r) ?? [];
  }
  if (useToplistFilter) {
    baseFilters.typeOfBookingListSort =
      state.typeOfBookingListSort?.map((r) => r) ?? [];
  }

  return baseFilters;
};

function AdminStatistics() {
  const [filtersReady, setFiltersReady] = useState(false);
  const [hasFetchedInitial, setHasFetchedInitial] = useState(false);
  const [memberExportType, setMemberExportType] = useState<MemberExportType>(
    MemberExportType.DEFAULT
  );

  const [refreshExports, setRefreshExports] = useState(0);
  const [presetFileName, setPresetFileName] = useState<string>("");
  const [user, setUser] = useState<IUser | undefined>(undefined);
  const [showChooseUserPopup, setShowChooseUserPopup] =
    useState<boolean>(false);
  const [showExportStatisticsPopup, setShowExportStatisticsPopup] =
    useState<boolean>(false);
  const [toggleSpecificFetch, setToggleSpecificFetch] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(new Date());
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([
    1, 2, 3, 4, 5, 6, 0,
  ]);
  const [exportType, setExportType] = useState<"guest" | "member" | null>(
    "member"
  );

  const [memberTypeOccupancyData, setMemberTypeOccupancyData] = useState<
    OccupancyStatisticsExport[]
  >([]);
  const [guestOccupancyData, setGuestOccupancyData] = useState<
    OccupancyStatisticsExport[]
  >([]);
  const [courtOccupancyData, setCourtOccupancyData] = useState<
    OccupancyStatisticsExport[]
  >([]);
  const [noShowOccupancyData, setNoShowOccupancyData] = useState<
    NoShowOccupancyStatisticsExport[]
  >([]);
  const [memberOccupancyData, setMemberOccupancyData] = useState<
    MemberStatisticsExport[]
  >([]);
  const [rehabOccupancyData, setRehabOccupancyData] = useState<
    OccupancyStatisticsExport[]
  >([]);
  const [coachOccupancyData, setCoachOccupancyData] = useState<
    CoachStatisticsExport[]
  >([]);
  const [activityOccupancyData, setActivityOccupancyData] = useState<
    ActivityOccupancyStatisticsExport[]
  >([]);

  const [bookedCourtsOccupancyData, setBookedCourtsOccupancyData] = useState<
    CourtOccupancyStatisticsExport[]
  >([]);

  const [chartType, setChartType] = useState<
    | "occupancy"
    | "members-occupancy"
    | "courts-occupancy"
    | "no-show-occupancy"
    | "activity-occupancy"
    | "rehab-occupancy"
    | "coach-occupancy"
    | "exports"
  >("occupancy");

  const memberTypeFilterStore = useMemo(
    () => createStatisticsFilterStore(),
    []
  );
  const guestTypeFilterStore = useMemo(() => createStatisticsFilterStore(), []);
  const bookedCourtsFilterStore = useMemo(
    () => createStatisticsFilterStore("percent"),
    []
  );

  const courtOccupancyFilterStore = useMemo(
    () => createStatisticsFilterStore(),
    []
  );
  const memberOccupancyFilterStore = useMemo(
    () => createStatisticsFilterStore(),
    []
  );
  const noShowOccupancyFilterStore = useMemo(
    () => createStatisticsFilterStore(),
    []
  );
  const rehabOccupancyFilterStore = useMemo(
    () => createStatisticsFilterStore(),
    []
  );
  const coachOccupancyFilterStore = useMemo(
    () => createStatisticsFilterStore(),
    []
  );

  const activityOccupancyFilterStore = useMemo(
    () => createStatisticsFilterStore(),
    []
  );
  type ExportPayload = {
    exportType?: OccupancyAggregationType | MemberExportType;
    filters?: any;
  };

  type ExportPayloadResult = ExportPayload | ExportPayload[];

  type ExportConfig = {
    resolver: string;
    buildPayload: () => ExportPayloadResult;
  };

  const EXPORT_CONFIG: Record<string, ExportConfig | undefined> = {
    occupancy: {
      resolver: "get_occupancy_slots_export",
      buildPayload: () => {
        const exports = [];

        if (exportType === "member") {
          exports.push({
            exportType: OccupancyAggregationType.MEMBER_TYPE,
            filters: buildFiltersFromStore(memberTypeFilterStore),
          });
        }

        if (exportType === "guest") {
          exports.push({
            exportType: OccupancyAggregationType.GUEST,
            filters: buildFiltersFromStore(guestTypeFilterStore),
          });
        }

        return exports;
      },
    },

    "members-occupancy": {
      resolver: "get_member_occupancy_slots_export",
      buildPayload: () => ({
        exportType: memberExportType,
        filters: buildFiltersFromStore(
          memberOccupancyFilterStore,
          true,
          true,
          true
        ),
      }),
    },

    "courts-occupancy": {
      resolver: "get_court_occupancy_slots_export",
      buildPayload: () => ({
        filters: buildFiltersFromStore(bookedCourtsFilterStore),
      }),
    },

    "no-show-occupancy": {
      resolver: "get_no_show_occupancy_slots_export",
      buildPayload: () => ({
        filters: buildFiltersFromStore(
          noShowOccupancyFilterStore,
          false,
          false
        ),
      }),
    },

    "activity-occupancy": {
      resolver: "get_activity_occupancy_slots_export",
      buildPayload: () => ({
        filters: buildFiltersFromStore(
          activityOccupancyFilterStore,
          false,
          false
        ),
      }),
    },

    "rehab-occupancy": {
      resolver: "get_rehab_occupancy_slots_export",
      buildPayload: () => ({
        filters: buildFiltersFromStore(rehabOccupancyFilterStore, false, false),
      }),
    },

    "coach-occupancy": {
      resolver: "get_coach_occupancy_slots_export",
      buildPayload: () => ({
        filters: buildFiltersFromStore(coachOccupancyFilterStore, false, true),
      }),
    },
  };

  const startExport = async (exportFileName: string) => {
    const config = EXPORT_CONFIG[chartType];

    if (!config) {
      toast.error("Ingen export tillgänglig för vald vy");
      return;
    }

    const payload = config.buildPayload();
    const exports = Array.isArray(payload) ? payload : [payload];

    try {
      toast.loading("Startar export...", { toastId: "exporting-statistics" });
      for (const exp of exports) {
        await exportStatisticsOccupancySlots(
          config.resolver,
          exp.exportType ?? OccupancyAggregationType.MEMBER_TYPE, // ignored if unused
          selectedStartDate.toISOString().slice(0, 10),
          selectedEndDate.toISOString().slice(0, 10),
          exportFileName,
          exp.filters,
          selectedWeekdays
        );
      }
    } catch (error) {
      toast.error("Fel vid export av statistik");
      console.error(error);
    } finally {
      toast.dismiss("exporting-statistics");
    }
  };

  const fetchMemberOccupancySlots = async () => {
    if (!selectedStartDate || !selectedEndDate) return;
    setMemberOccupancyData([]);

    toast.loading("Hämtar medlemsbeläggningsdata...", {
      toastId: "fetching-member-occupancy",
    });

    try {
      const start = selectedStartDate.toISOString().slice(0, 10);
      const end = selectedEndDate.toISOString().slice(0, 10);
      const memberFilters = buildFiltersFromStore(
        memberOccupancyFilterStore,
        true,
        true,
        true
      );

      const member = await getMemberOccupancySlots(
        start,
        end,
        memberFilters,
        selectedWeekdays
      );

      setMemberOccupancyData(member);

      toast.dismiss("fetching-member-occupancy");

      toast.success("Medlemsbeläggningsdata hämtad");
    } catch (error) {
      toast.dismiss("fetching-member-occupancy");
      toast.error("Fel vid hämtning av medlemsbeläggningsdata");
      console.error("Error fetching member occupancy slots:", error);
    }
  };

  const fetchCoachOccupancySlots = async () => {
    if (!selectedStartDate || !selectedEndDate) return;
    setCoachOccupancyData([]);

    toast.loading("Hämtar tränarbeläggningsdata...", {
      toastId: "fetching-coach-occupancy",
    });

    try {
      const start = selectedStartDate.toISOString().slice(0, 10);
      const end = selectedEndDate.toISOString().slice(0, 10);
      const coachFilters = buildFiltersFromStore(
        coachOccupancyFilterStore,
        false,
        true
      );

      const coach = await getCoachOccupancySlots(
        start,
        end,
        coachFilters,
        selectedWeekdays
      );

      setCoachOccupancyData(coach);

      toast.dismiss("fetching-coach-occupancy");

      toast.success("Tränarbeläggningsdata hämtad");
    } catch (error) {
      toast.dismiss("fetching-coach-occupancy");
      toast.error("Fel vid hämtning av tränarbeläggningsdata");
      console.error("Error fetching coach occupancy slots:", error);
    }
  };

  const fetchOccupancySlots = async () => {
    if (!selectedStartDate || !selectedEndDate) return;
    setMemberTypeOccupancyData([]);
    setGuestOccupancyData([]);
    setCourtOccupancyData([]);

    if (hasFetchedInitial) {
      toast.loading("Hämtar beläggningsdata...", {
        toastId: "fetching-occupancy",
      });
    }

    try {
      const start = selectedStartDate.toISOString().slice(0, 10);
      const end = selectedEndDate.toISOString().slice(0, 10);
      // Each store converted to backend-friendly filters
      const memberFilters = buildFiltersFromStore(memberTypeFilterStore);
      const guestFilters = buildFiltersFromStore(guestTypeFilterStore);
      const courtFilters = buildFiltersFromStore(bookedCourtsFilterStore);
      // Fetch one type at a time (more stable & avoids large payloads)
      const [member, guest, court] = await Promise.all([
        getOccupancySlots(
          start,
          end,
          [OccupancyAggregationType.MEMBER_TYPE],
          memberFilters,
          selectedWeekdays
        ),
        getOccupancySlots(
          start,
          end,
          [OccupancyAggregationType.GUEST],
          guestFilters,
          selectedWeekdays
        ),
        getOccupancySlots(
          start,
          end,
          [OccupancyAggregationType.COURT],
          courtFilters,
          selectedWeekdays
        ),
      ]);

      setMemberTypeOccupancyData(member);
      setGuestOccupancyData(guest);
      setCourtOccupancyData(court);

      toast.dismiss("fetching-occupancy");
      if (hasFetchedInitial) {
        toast.success("Beläggningsdata hämtad");
      }
    } catch (error) {
      toast.dismiss("fetching-occupancy");
      toast.error("Fel vid hämtning av beläggningsdata");
      console.error("Error fetching occupancy slots:", error);
    }
  };

  const fetchRehabOccupancySlots = async () => {
    if (!selectedStartDate || !selectedEndDate) return;
    setRehabOccupancyData([]);

    toast.loading("Hämtar rehab-bad beläggningsdata...", {
      toastId: "fetching-rehab-occupancy",
    });

    try {
      const start = selectedStartDate.toISOString().slice(0, 10);
      const end = selectedEndDate.toISOString().slice(0, 10);
      const rehabFilters = buildFiltersFromStore(
        rehabOccupancyFilterStore,
        false,
        false
      );

      const rehab = await getRehabOccupancySlots(
        start,
        end,
        rehabFilters,
        selectedWeekdays
      );

      setRehabOccupancyData(rehab);

      toast.dismiss("fetching-rehab-occupancy");

      toast.success("Rehab-bad beläggningsdata hämtad");
    } catch (error) {
      toast.dismiss("fetching-rehab-occupancy");
      toast.error("Fel vid hämtning av rehab-bad beläggningsdata");
      console.error("Error fetching rehab occupancy slots:", error);
    }
  };

  const fetchCourtOccupancySlots = async () => {
    if (!selectedStartDate || !selectedEndDate) return;
    setCourtOccupancyData([]);

    toast.loading("Hämtar banbeläggningsdata...", {
      toastId: "fetching-court-occupancy",
    });

    try {
      const start = selectedStartDate.toISOString().slice(0, 10);
      const end = selectedEndDate.toISOString().slice(0, 10);
      const courtFilters = buildFiltersFromStore(bookedCourtsFilterStore);

      const court = await getCourtOccupancySlots(
        start,
        end,
        courtFilters,
        selectedWeekdays
      );

      setBookedCourtsOccupancyData(court);

      toast.dismiss("fetching-court-occupancy");

      toast.success("Banbeläggningsdata hämtad");
    } catch (error) {
      toast.dismiss("fetching-court-occupancy");
      toast.error("Fel vid hämtning av banbeläggningsdata");
      console.error("Error fetching court occupancy slots:", error);
    }
  };

  const fetchActivityOccupancySlots = async () => {
    if (!selectedStartDate || !selectedEndDate) return;
    setActivityOccupancyData([]);

    toast.loading("Hämtar aktivitetsbeläggningsdata...", {
      toastId: "fetching-activity-occupancy",
    });

    try {
      const start = selectedStartDate.toISOString().slice(0, 10);
      const end = selectedEndDate.toISOString().slice(0, 10);
      const activityFilters = buildFiltersFromStore(
        activityOccupancyFilterStore,
        false,
        false
      );

      const activity = await getActivityOccupancySlots(
        start,
        end,
        activityFilters,
        selectedWeekdays
      );

      setActivityOccupancyData(activity);

      toast.dismiss("fetching-activity-occupancy");

      toast.success("Aktivitetsbeläggningsdata hämtad");
    } catch (error) {
      toast.dismiss("fetching-activity-occupancy");
      toast.error("Fel vid hämtning av aktivitetsbeläggningsdata");
      console.error("Error fetching activity occupancy slots:", error);
    }
  };
  const fetchNoShowOccupancySlots = async () => {
    if (!selectedStartDate || !selectedEndDate) return;
    setNoShowOccupancyData([]);

    toast.loading("Hämtar no show beläggningsdata...", {
      toastId: "fetching-no-show-occupancy",
    });

    try {
      const start = selectedStartDate.toISOString().slice(0, 10);
      const end = selectedEndDate.toISOString().slice(0, 10);
      const noShowFilters = buildFiltersFromStore(
        noShowOccupancyFilterStore,
        false,
        false
      );
      const noShow = await getNoShowOccupancySlots(start, end, noShowFilters);
      setNoShowOccupancyData(noShow);
      toast.dismiss("fetching-no-show-occupancy");

      toast.success("No show beläggningsdata hämtad");
    } catch (error) {
      toast.dismiss("fetching-no-show-occupancy");
      toast.error("Fel vid hämtning av no show beläggningsdata");
      console.error("Error fetching no show occupancy slots:", error);
    }
  };

  const initialPermissionsList = [
    { permission_id: "1000" },
    { permission_id: "7000" },
    { permission_id: "7700" },
    { permission_id: "2500" },
    { permission_id: "1500" },
    { permission_id: "1050" },
  ];
  const fetchChosenItems = async ({
    fetchCourts = false,
    fetchPermissions = false,
    fetchCoaches = false,
  }) => {
    try {
      const response = await combinedQuery({
        active: true,
        requestor: "admin",
        fetchCourts: fetchCourts,
        fetchPermissions: fetchPermissions,
        fetchCoaches: fetchCoaches,
      });

      if (
        fetchPermissions &&
        response.permissions &&
        !("message" in response.permissions)
      ) {
        const filteredPermissions = response.permissions.filter((p) =>
          initialPermissionsList.some(
            (ip) => ip.permission_id === p.permission_id
          )
        );

        const stores = [
          memberTypeFilterStore,
          guestTypeFilterStore,
          memberOccupancyFilterStore,
          bookedCourtsFilterStore,
          noShowOccupancyFilterStore,
          rehabOccupancyFilterStore,
          coachOccupancyFilterStore,
          activityOccupancyFilterStore,
        ];

        for (const store of stores) {
          store.setState({
            permissions: response.permissions,
            initialPermissions: filteredPermissions,
            selectedPermissions: filteredPermissions,
          });
        }
      }

      if (fetchCourts && response.courts && !("message" in response.courts)) {
        const stores = [
          memberTypeFilterStore,
          guestTypeFilterStore,
          bookedCourtsFilterStore,
          courtOccupancyFilterStore,
        ];

        for (const store of stores) {
          store.setState({
            selectedCourts: response.courts,
            courts: response.courts,
          });
        }
      }

      if (
        fetchCoaches &&
        response.coaches &&
        !("message" in response.coaches)
      ) {
        const stores = [
          courtOccupancyFilterStore,
          coachOccupancyFilterStore,
          activityOccupancyFilterStore,
          bookedCourtsFilterStore,
        ];

        for (const store of stores) {
          if (store === bookedCourtsFilterStore) {
            store.setState({
              coaches: response.coaches,
            });
            continue;
          }
          store.setState({
            coaches: response.coaches,
            selectedCoaches: response.coaches,
          });
        }
      }
    } catch (error: any) {
      toast.error(error);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchChosenItems({
        fetchCourts: true,
        fetchPermissions: true,
        fetchCoaches: true,
      });
      setFiltersReady(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filtersReady || hasFetchedInitial) return;

    fetchOccupancySlots();
    setHasFetchedInitial(true);
  }, [filtersReady, hasFetchedInitial]);

  useEffect(() => {
    const start = selectedStartDate.toISOString().slice(0, 10);
    const end = selectedEndDate.toISOString().slice(0, 10);

    let label = "";

    if (chartType === "occupancy") {
      if (exportType === "member") label = "Beläggning_Medlemskap";
      else if (exportType === "guest") label = "Beläggning_Gäst";
      else label = "Beläggning";
    } else {
      const baseMap = {
        "members-occupancy": "Medlemmar",
        "courts-occupancy": "Banor",
        "no-show-occupancy": "No-show",
        "activity-occupancy": "Aktiviteter",
        "rehab-occupancy": "Rehab-bad",
        "coach-occupancy": "Tränare",
        exports: "Export",
      } as const;

      label = baseMap[chartType];
    }

    setPresetFileName(`${label}_${start}_till_${end}`);
  }, [chartType, exportType, selectedStartDate, selectedEndDate]);

  return (
    <div className="admin-handle-statistic-layout">
      <div className="handle-statistic-actions-wrapper">
        <div className="chart-actions-wrapper text-m">
          <div className="chart-actions-left">
            <button
              className={`toggle-button ${
                chartType === "occupancy" ? "active" : ""
              }`}
              onClick={() => setChartType("occupancy")}
            >
              Beläggning
            </button>
            <button
              className={`toggle-button ${
                chartType === "members-occupancy" ? "active" : ""
              }`}
              onClick={() => setChartType("members-occupancy")}
            >
              Medlemmar
            </button>
            <button
              className={`toggle-button ${
                chartType === "courts-occupancy" ? "active" : ""
              }`}
              onClick={() => setChartType("courts-occupancy")}
            >
              Banor
            </button>
            <button
              className={`toggle-button ${
                chartType === "no-show-occupancy" ? "active" : ""
              }`}
              onClick={() => setChartType("no-show-occupancy")}
            >
              No show
            </button>
            <button
              className={`toggle-button ${
                chartType === "activity-occupancy" ? "active" : ""
              }`}
              onClick={() => setChartType("activity-occupancy")}
            >
              Aktiviteter
            </button>
            <button
              className={`toggle-button ${
                chartType === "rehab-occupancy" ? "active" : ""
              }`}
              onClick={() => setChartType("rehab-occupancy")}
            >
              Rehab-bad
            </button>
            <button
              className={`toggle-button ${
                chartType === "coach-occupancy" ? "active" : ""
              }`}
              onClick={() => setChartType("coach-occupancy")}
            >
              Tränare
            </button>
            <button
              className={`toggle-button ${
                chartType === "exports" ? "active" : ""
              }`}
              onClick={() => setChartType("exports")}
            >
              Exporter
            </button>
          </div>

          <div className="chart-actions-right">
            <WeekDaySelector
              value={selectedStartDate}
              onChange={(value) => setSelectedStartDate(value)}
              includeWeekdays={false}
              textM
              isSmall
            />
            <WeekDaySelector
              value={selectedEndDate}
              onChange={(value) => setSelectedEndDate(value)}
              includeWeekdays={false}
              includeWeekdayFilter
              selectedWeekdays={selectedWeekdays}
              onWeekdayToggle={(day) => {
                if (selectedWeekdays.includes(day)) {
                  setSelectedWeekdays(
                    selectedWeekdays.filter((d) => d !== day)
                  );
                } else {
                  setSelectedWeekdays([...selectedWeekdays, day]);
                }
              }}
              textM
              isSmall
            />
            <button
              className={``}
              onClick={() => {
                if (chartType === "exports") {
                  setRefreshExports((v) => v + 1);
                  return;
                }
                chartType === "occupancy" && fetchOccupancySlots();
                chartType === "rehab-occupancy" && fetchRehabOccupancySlots();
                chartType === "courts-occupancy" && fetchCourtOccupancySlots();
                chartType === "members-occupancy" &&
                  fetchMemberOccupancySlots();

                setToggleSpecificFetch(!toggleSpecificFetch);
                chartType === "no-show-occupancy" &&
                  fetchNoShowOccupancySlots();
                chartType === "activity-occupancy" &&
                  fetchActivityOccupancySlots();
                chartType === "coach-occupancy" && fetchCoachOccupancySlots();
              }}
            >
              Hämta
            </button>
            <button
              className={``}
              onClick={() => setShowExportStatisticsPopup(true)}
              disabled={chartType === "exports"}
            >
              Exportera
            </button>

            {/* <div className="include-activity-wrapper">
              <span className="text-m">Inkludera aktiviteter</span>
              <label className="toggle text-m">
                <input
                  type="checkbox"
                  checked={includeActivity}
                  onChange={(e) => setIncludeActivity(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
            </div> */}
          </div>
        </div>
      </div>
      <div className="statistic-modules-wrapper">
        {chartType === "occupancy" && (
          <AdminOccupancyModule
            memberTypeOccupancyData={memberTypeOccupancyData[0]}
            guestOccupancyData={guestOccupancyData[0]}
            courtOccupancyData={courtOccupancyData[0]}
            memberTypeFilterStore={memberTypeFilterStore}
            guestTypeFilterStore={guestTypeFilterStore}
            bookedCourtsFilterStore={bookedCourtsFilterStore}
            startDate={selectedStartDate.toISOString().slice(0, 10)}
            endDate={selectedEndDate.toISOString().slice(0, 10)}
            selectedWeekdays={selectedWeekdays}
          />
        )}
        {chartType === "rehab-occupancy" && (
          <AdminRehabOccupancyModule
            rehabOccupancyData={rehabOccupancyData[0]}
            rehabOccupancyFilterStore={rehabOccupancyFilterStore}
            startDate={selectedStartDate.toISOString().slice(0, 10)}
            endDate={selectedEndDate.toISOString().slice(0, 10)}
            selectedWeekdays={selectedWeekdays}
          />
        )}
        {chartType === "members-occupancy" && (
          <AdminMembersOccupancyModule
            memberOccupancyData={memberOccupancyData[0]}
            memberOccupancyFilterStore={memberOccupancyFilterStore}
            startDate={selectedStartDate.toISOString().slice(0, 10)}
            endDate={selectedEndDate.toISOString().slice(0, 10)}
            selectedWeekdays={selectedWeekdays}
            toggleSpecificFetch={toggleSpecificFetch}
            user={user}
            setShowChooseUserPopup={setShowChooseUserPopup}
          />
        )}
        {chartType === "courts-occupancy" && (
          <AdminCourtsOccupancyModule
            courtsOccupancyData={bookedCourtsOccupancyData[0]}
            courtOccupancyFilterStore={bookedCourtsFilterStore}
            startDate={selectedStartDate.toISOString().slice(0, 10)}
            endDate={selectedEndDate.toISOString().slice(0, 10)}
            selectedWeekdays={selectedWeekdays}
          />
        )}
        {chartType === "no-show-occupancy" && (
          <AdminNoShowOccupancyModule
            noShowOccupancyData={noShowOccupancyData[0]}
            noShowOccupancyFilterStore={noShowOccupancyFilterStore}
            startDate={selectedStartDate.toISOString().slice(0, 10)}
            endDate={selectedEndDate.toISOString().slice(0, 10)}
            selectedWeekdays={selectedWeekdays}
          />
        )}
        {chartType === "coach-occupancy" && (
          <AdminCoachOccupancyModule
            coachOccupancyData={coachOccupancyData[0]}
            coachOccupancyFilterStore={coachOccupancyFilterStore}
            startDate={selectedStartDate.toISOString().slice(0, 10)}
            endDate={selectedEndDate.toISOString().slice(0, 10)}
            selectedWeekdays={selectedWeekdays}
          />
        )}
        {chartType === "activity-occupancy" && (
          <AdminActivityOccupancyModule
            activityOccupancyData={activityOccupancyData[0]}
            activityOccupancyFilterStore={activityOccupancyFilterStore}
            startDate={selectedStartDate.toISOString().slice(0, 10)}
            endDate={selectedEndDate.toISOString().slice(0, 10)}
            selectedWeekdays={selectedWeekdays}
          />
        )}
        {chartType === "exports" && (
          <AdminExportsModule refreshKey={refreshExports} />
        )}
      </div>
      <ChooseUserPopup
        showPopup={showChooseUserPopup}
        onClose={() => setShowChooseUserPopup(false)}
        onConfirm={(user) => {
          setUser(user);
        }}
        onCancel={() => setShowChooseUserPopup(false)}
      />
      <ExportStatisticsPopup
        showPopup={showExportStatisticsPopup}
        onClose={() => setShowExportStatisticsPopup(false)}
        onConfirm={(exportFileName) => {
          setShowExportStatisticsPopup(false);
          startExport(exportFileName);
        }}
        presetFileName={presetFileName}
        children={
          chartType === "occupancy" ? (
            <div className="handle-export-children-wrapper ">
              <div className="handle-export-child">
                <span className="text-m secondary-text">
                  Beläggning - Medlemskap
                </span>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={exportType === "member"}
                    onChange={() =>
                      setExportType(exportType === "member" ? null : "member")
                    }
                  />
                  <span className="slider round"></span>
                </label>
              </div>
              <div className="handle-export-child">
                <span className="text-m secondary-text">Beläggning - Gäst</span>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={exportType === "guest"}
                    onChange={() =>
                      setExportType(exportType === "guest" ? null : "guest")
                    }
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          ) : (
            chartType === "members-occupancy" && (
              <div className="handle-export-children-wrapper ">
                <div className="handle-export-child">
                  <span className="text-m secondary-text">
                    Inkludera medlemmar med 0 bokningar i export
                  </span>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={memberExportType === "INCLUDE_NULL"}
                      onChange={() =>
                        setMemberExportType(
                          memberExportType === MemberExportType.INCLUDE_NULL
                            ? MemberExportType.DEFAULT
                            : MemberExportType.INCLUDE_NULL
                        )
                      }
                    />

                    <span className="slider round"></span>
                  </label>
                </div>
              </div>
            )
          )
        }
      />
    </div>
  );
}

export default AdminStatistics;
